/**
 * ContasPagarPage Component
 *
 * Página de listagem e gerenciamento de contas a pagar.
 * Permite visualizar, criar, editar, excluir e dar baixa em contas a pagar de contas físicas.
 *
 * Funcionalidades:
 * - Listagem de contas a pagar
 * - Filtros por status, origem, classificação e vencimento
 * - Modal de criação/edição
 * - Exclusão com confirmação
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  Edit, Trash2, Plus, Search, Filter, Download, Upload,
  X, Check, CheckCircle2, AlertCircle, Layers, Sparkles,
  MoreVertical, Calendar, Copy, Unlink, FileSpreadsheet, DollarSign
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import FilterLeftPicker from '../../../../components/ERP/FilterLeftPicker'
import ERPTable from '../../../../components/ERP/ERPTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import SelectPersonModal from '../../../../components/ERP/Modal/SelectPersonModal'
import {
  accountPayableService,
  supplierService,
  customerService,
  userService,
  cashStatementService,
  financialCategoryService,
  costCenterService,
  financialAccountService
} from '../../../../services/erpApi'
import Toast from '../../../../components/Toast/Toast'
import { useToast } from '../../../../hooks/useToast'
import { formatDateOnly, parseDateOnly, toDateInputValue } from '../../../../utils/dateUtils'
import { getPayableRemainingBalance, isPartiallyPaidTitle } from '../../../../utils/erpTitlePayment'
import './ContasPagarPage.css'

const PERSON_TYPE_LABELS = {
  supplier: 'Fornecedor',
  customer: 'Cliente',
  user: 'Funcionário'
}

const FILTER_NONE_CATEGORY = '__none_category__'
const FILTER_NONE_COST_CENTER = '__none_cost_center__'

function resolvePersonFields(conta) {
  if (conta.customer_id) {
    return {
      personType: 'customer',
      personId: String(conta.customer_id),
      personName: conta.customer_name || ''
    }
  }
  if (conta.user_id) {
    return {
      personType: 'user',
      personId: String(conta.user_id),
      personName: conta.user_name || ''
    }
  }
  return {
    personType: 'supplier',
    personId: conta.supplier_id ? String(conta.supplier_id) : '',
    personName: conta.supplier_name || ''
  }
}

function resolveCategoryFields(categoryId, categorias) {
  if (!categoryId) {
    return { categoryId: '', subcategoryId: '' }
  }
  const cat = categorias.find((c) => Number(c.id) === Number(categoryId))
  if (!cat) {
    return { categoryId: String(categoryId), subcategoryId: '' }
  }
  if (cat.parent_id) {
    return { categoryId: String(cat.parent_id), subcategoryId: String(cat.id) }
  }
  return { categoryId: String(cat.id), subcategoryId: '' }
}

function canSettlePhysicalPayable(conta) {
  if (!conta) return false
  if (conta.status === 'paid' || conta.status === 'cancelled') return false
  if (conta.financial_account_type === 'digital') return false
  return getPayableRemainingBalance(conta) > 0.01
}

function ContasPagarPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [contas, setContas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingConta, setEditingConta] = useState(null)
  const [fornecedores, setFornecedores] = useState([])
  const [clientes, setClientes] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [categorias, setCategorias] = useState([])
  const [centrosCusto, setCentrosCusto] = useState([])
  const [openMenuId, setOpenMenuId] = useState(null)
  const [menuPosition, setMenuPosition] = useState(null)
  const [baixaModalOpen, setBaixaModalOpen] = useState(false)
  const [contaParaBaixa, setContaParaBaixa] = useState(null)
  const [baixaSubmitting, setBaixaSubmitting] = useState(false)
  const [contasFisicas, setContasFisicas] = useState([])
  const [contasBancarias, setContasBancarias] = useState([])
  const [baixaFormData, setBaixaFormData] = useState({
    paidAmount: '',
    paymentDate: toDateInputValue(new Date()),
    financialAccountId: ''
  })

  // Estados para importação Excel
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importStep, setImportStep] = useState('upload') // upload | preview | result
  const [importFile, setImportFile] = useState(null)
  const [importPreview, setImportPreview] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const [importLoading, setImportLoading] = useState(false)
  const importFileInputRef = useRef(null)
  
  // Estados para Toast
  const { toast, showToast, hideToast } = useToast()
  
  // Estado para controlar qual código foi copiado
  const [copiedId, setCopiedId] = useState(null)
  const [showPersonModal, setShowPersonModal] = useState(false)
  const [formData, setFormData] = useState({
    personType: 'supplier',
    personId: '',
    personName: '',
    description: '',
    amount: '',
    dueDate: '',
    status: 'pending',
    categoryId: '',
    subcategoryId: '',
    costCenterId: '',
    financialAccountId: '',
    interestRate: 0,
    fineRate: 0,
    observations: ''
  })
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    status: ['pending', 'overdue'],
    supplierId: '',
    customerId: '',
    userId: '',
    originType: '',
    categoryFilterId: '',
    subcategoryFilterId: '',
    costCenterId: '',
    dueDateFrom: '',
    dueDateTo: ''
  })
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const periodBtnRef = useRef(null)

  const loadContas = useCallback(async () => {
    setLoading(true)
    try {
      const {
        categoryFilterId,
        subcategoryFilterId,
        costCenterId,
        ...rest
      } = filters

      const params = { ...rest }
      if (costCenterId === FILTER_NONE_COST_CENTER) {
        params.withoutCostCenter = 'true'
      } else if (costCenterId) {
        params.costCenterId = costCenterId
      }

      if (categoryFilterId === FILTER_NONE_CATEGORY) {
        params.withoutCategory = 'true'
      } else if (subcategoryFilterId) {
        params.categoryId = subcategoryFilterId
      } else if (categoryFilterId) {
        params.categoryId = categoryFilterId
        params.includeSubcategories = 'true'
      }

      const response = await accountPayableService.getAll(params)
      if (response.success) {
        setContas(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar contas a pagar:', error)
    } finally {
      setLoading(false)
    }
  }, [filters])

  const loadFornecedores = useCallback(async () => {
    try {
      const response = await supplierService.getAll({ status: 'active', limit: 1000 })
      if (response.success) {
        setFornecedores(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error)
    }
  }, [])

  const loadClientes = useCallback(async () => {
    try {
      const response = await customerService.getAll({ limit: 1000 })
      if (response.success) {
        setClientes(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
    }
  }, [])

  const loadUsuarios = useCallback(async () => {
    try {
      const response = await userService.getAll({ limit: 1000 })
      if (response.success) {
        setUsuarios(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
    }
  }, [])

  const loadContasFisicas = useCallback(async () => {
    try {
      const response = await financialAccountService.getAll({ active: true, limit: 200 })
      if (response.success) {
        const accounts = response.data || []
        setContasBancarias(accounts)
        setContasFisicas(accounts.filter((account) => account.account_type === 'physical'))
      }
    } catch (error) {
      console.error('Erro ao carregar contas financeiras:', error)
    }
  }, [])

  const loadClassificacaoOptions = useCallback(async () => {
    try {
      const [categoriasRes, centrosRes] = await Promise.all([
        financialCategoryService.getAll({ type: 'expense', active: true }),
        costCenterService.getAll({ active: true })
      ])
      if (categoriasRes.success) setCategorias(categoriasRes.data || [])
      if (centrosRes.success) setCentrosCusto(centrosRes.data || [])
    } catch (error) {
      console.error('Erro ao carregar categorias/centros de custo:', error)
    }
  }, [])

  const categoriasPrincipais = useMemo(
    () => categorias.filter((c) => !c.parent_id),
    [categorias]
  )

  const subcategoriasFiltradas = useMemo(
    () =>
      categorias.filter(
        (c) => c.parent_id && Number(c.parent_id) === Number(formData.categoryId)
      ),
    [categorias, formData.categoryId]
  )

  const filterSubcategorias = useMemo(
    () =>
      categorias.filter(
        (c) => c.parent_id && Number(c.parent_id) === Number(filters.categoryFilterId)
      ),
    [categorias, filters.categoryFilterId]
  )

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Selecione uma categoria' },
      ...categoriasPrincipais.map((c) => ({ value: String(c.id), label: c.name }))
    ],
    [categoriasPrincipais]
  )

  const subcategoryOptions = useMemo(
    () => [
      { value: '', label: 'Selecione uma subcategoria' },
      ...subcategoriasFiltradas.map((c) => ({ value: String(c.id), label: c.name }))
    ],
    [subcategoriasFiltradas]
  )

  const costCenterOptions = useMemo(
    () => [
      { value: '', label: 'Selecione um centro de custo' },
      ...centrosCusto.map((c) => ({ value: String(c.id), label: c.name }))
    ],
    [centrosCusto]
  )

  const financialAccountOptions = useMemo(() => {
    const options = contasBancarias.map((account) => ({
      value: String(account.id),
      label: account.code ? `${account.name} (${account.code})` : account.name
    }))
    const selectedId = formData.financialAccountId
    if (selectedId && !options.some((option) => option.value === String(selectedId))) {
      options.unshift({
        value: String(selectedId),
        label: editingConta?.financial_account_name || 'Conta vinculada'
      })
    }
    return [
      { value: '', label: 'Selecione uma conta bancária' },
      ...options
    ]
  }, [contasBancarias, formData.financialAccountId, editingConta])

  const categoryFilterOptions = useMemo(
    () => categoriasPrincipais.map((c) => ({ value: String(c.id), label: c.name })),
    [categoriasPrincipais]
  )

  const subcategoryFilterOptions = useMemo(
    () => filterSubcategorias.map((c) => ({ value: String(c.id), label: c.name })),
    [filterSubcategorias]
  )

  const costCenterFilterOptions = useMemo(
    () => centrosCusto.map((c) => ({ value: String(c.id), label: c.name })),
    [centrosCusto]
  )

  const supplierFilterOptions = useMemo(
    () => fornecedores.map((f) => ({ value: String(f.id), label: f.name })),
    [fornecedores]
  )

  const customerFilterOptions = useMemo(
    () => clientes.map((c) => ({ value: String(c.id), label: c.name })),
    [clientes]
  )

  const userFilterOptions = useMemo(
    () => usuarios.map((u) => ({ value: String(u.id), label: u.name })),
    [usuarios]
  )

  useEffect(() => {
    loadContas()
    loadFornecedores()
    loadClientes()
    loadUsuarios()
    loadClassificacaoOptions()
    loadContasFisicas()
  }, [loadContas, loadFornecedores, loadClientes, loadUsuarios, loadClassificacaoOptions, loadContasFisicas])

  useEffect(() => {
    const search = location.state?.search || searchParams.get('search')
    const dashboardPreset = location.state?.dashboardPreset

    let hasStateToClear = false

    if (search) {
      hasStateToClear = hasStateToClear || Boolean(location.state?.search)
      setFilters((prev) => ({ ...prev, search: String(search) }))
    }

    if (dashboardPreset === 'next7days') {
      hasStateToClear = true
      setFilters((prev) => ({
        ...prev,
        status: ['pending', 'overdue']
      }))
    }

    if (dashboardPreset === 'dateRange') {
      const dueDateFrom = location.state?.dueDateFrom
      const dueDateTo = location.state?.dueDateTo
      if (dueDateFrom && dueDateTo) {
        hasStateToClear = true
        setFilters((prev) => ({
          ...prev,
          status: ['pending', 'overdue'],
          dueDateFrom,
          dueDateTo
        }))
      }
    }

    if (dashboardPreset === 'overdue') {
      hasStateToClear = true
      setFilters((prev) => ({
        ...prev,
        status: ['overdue']
      }))
    }

    if (hasStateToClear) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate, searchParams])

  // Reaplica categoria/subcategoria quando as opções carregam após abrir o Editar
  useEffect(() => {
    if (!editingConta || !modalOpen || categorias.length === 0) return
    const { categoryId, subcategoryId } = resolveCategoryFields(
      editingConta.category_id,
      categorias
    )
    setFormData((prev) => {
      if (prev.categoryId === categoryId && prev.subcategoryId === subcategoryId) {
        return prev
      }
      return { ...prev, categoryId, subcategoryId }
    })
  }, [editingConta, modalOpen, categorias])

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.erp-table-row-menu-btn') && !e.target.closest('.contas-pagar-dropdown-menu')) {
        setOpenMenuId(null)
        setMenuPosition(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openMenuId])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        supplierId: formData.personType === 'supplier' ? formData.personId || null : null,
        customerId: formData.personType === 'customer' ? formData.personId || null : null,
        userId: formData.personType === 'user' ? formData.personId || null : null,
        description: formData.description || null,
        amount: parseFloat(formData.amount) || 0,
        dueDate: formData.dueDate || null,
        status: formData.status || 'pending',
        categoryId: formData.subcategoryId || formData.categoryId || null,
        costCenterId: formData.costCenterId || null,
        financialAccountId: formData.financialAccountId || null,
        interestRate: parseFloat(formData.interestRate) || 0,
        fineRate: parseFloat(formData.fineRate) || 0,
        observations: formData.observations || null
      }

      let response
      if (editingConta) {
        response = await accountPayableService.update(editingConta.id, dataToSend)
      } else {
        response = await accountPayableService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadContas()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar conta' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar conta:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar conta'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleSelectPerson = (person) => {
    setFormData((prev) => ({
      ...prev,
      personType: person.type,
      personId: String(person.id),
      personName: person.name
    }))
  }

  const handleEdit = (conta) => {
    const { categoryId, subcategoryId } = resolveCategoryFields(conta.category_id, categorias)
    const person = resolvePersonFields(conta)
    setEditingConta(conta)
    setFormData({
      ...person,
      description: conta.description || '',
      amount: conta.amount || '',
      dueDate: conta.due_date ? conta.due_date.split('T')[0] : '',
      status: conta.status || 'pending',
      categoryId,
      subcategoryId,
      costCenterId: conta.cost_center_id ? String(conta.cost_center_id) : '',
      financialAccountId: conta.financial_account_id ? String(conta.financial_account_id) : '',
      interestRate: conta.interest_rate || 0,
      fineRate: conta.fine_rate || 0,
      observations: conta.observations || ''
    })
    setModalOpen(true)
  }

  const handleDelete = async (conta) => {
    if (window.confirm(`Deseja realmente excluir a conta "${conta.description || conta.id}"?`)) {
      try {
        const response = await accountPayableService.delete(conta.id)
        if (response.success) {
          loadContas()
        }
      } catch (error) {
        console.error('Erro ao excluir conta:', error)
        alert('Erro ao excluir conta')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      personType: 'supplier',
      personId: '',
      personName: '',
      description: '',
      amount: '',
      dueDate: '',
      status: 'pending',
      categoryId: '',
      subcategoryId: '',
      costCenterId: '',
      financialAccountId: '',
      interestRate: 0,
      fineRate: 0,
      observations: ''
    })
    setEditingConta(null)
    setErrors({})
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const getStatusBadge = (status, dueDate, row) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const due = parseDateOnly(dueDate)
    const isOverdue = due && due < today && status !== 'paid'

    if (status === 'paid') {
      return <span className="status-badge paid">Pago</span>
    } else if (row && isPartiallyPaidTitle(row)) {
      return <span className="status-badge partial">Parcialmente pago</span>
    } else if (isOverdue || status === 'overdue') {
      return <span className="status-badge overdue">Vencido</span>
    } else if (status === 'cancelled') {
      return <span className="status-badge cancelled">Cancelado</span>
    } else {
      return <span className="status-badge pending">Pendente</span>
    }
  }

  const getOriginName = (row) => {
    if (row.supplier_id) {
      if (row.supplier_name) {
        return `Fornecedor: ${row.supplier_name}`
      }
      const fornecedor = fornecedores.find(f => Number(f.id) === Number(row.supplier_id))
      return fornecedor ? `Fornecedor: ${fornecedor.name}` : '-'
    }
    if (row.customer_id) {
      if (row.customer_name) {
        return `Cliente: ${row.customer_name}`
      }
      const cliente = clientes.find(c => Number(c.id) === Number(row.customer_id))
      return cliente ? `Cliente: ${cliente.name}` : '-'
    }
    if (row.user_id) {
      if (row.user_name) {
        return `Funcionário: ${row.user_name}`
      }
      const usuario = usuarios.find(u => Number(u.id) === Number(row.user_id))
      return usuario ? `Funcionário: ${usuario.name}` : '-'
    }
    return '-'
  }

  const hasActiveFilters = useMemo(() => {
    const defaultStatus = ['pending', 'overdue']
    const statusChanged = JSON.stringify([...filters.status].sort()) !== JSON.stringify(defaultStatus.sort())
    const periodChanged = Boolean(filters.dueDateFrom && filters.dueDateTo)
    return statusChanged ||
           filters.supplierId !== '' ||
           filters.customerId !== '' ||
           filters.userId !== '' ||
           filters.originType !== '' ||
           filters.categoryFilterId !== '' ||
           filters.subcategoryFilterId !== '' ||
           filters.costCenterId !== '' ||
           periodChanged
  }, [filters])

  const hasDateRangeFilter = Boolean(filters.dueDateFrom && filters.dueDateTo)

  const resetFilters = () => {
    setFilters({
      search: '',
      status: ['pending', 'overdue'],
      supplierId: '',
      customerId: '',
      userId: '',
      originType: '',
      categoryFilterId: '',
      subcategoryFilterId: '',
      costCenterId: '',
      dueDateFrom: '',
      dueDateTo: ''
    })
    setIsPeriodOpen(false)
  }

  const handlePeriodRangeChange = (start, end) => {
    let from = start
    let to = end
    if (from && to && from > to) {
      [from, to] = [to, from]
    }
    setFilters((prev) => ({
      ...prev,
      dueDateFrom: from,
      dueDateTo: to
    }))
  }

  const handleClearPeriodFilter = () => {
    setFilters((prev) => ({
      ...prev,
      dueDateFrom: '',
      dueDateTo: ''
    }))
  }

  const toggleStatusFilter = (statusValue) => {
    setFilters(prev => {
      const currentStatus = Array.isArray(prev.status) ? prev.status : [prev.status].filter(Boolean)
      const isSelected = currentStatus.includes(statusValue)
      
      let newStatus
      if (isSelected) {
        // Remover o status
        newStatus = currentStatus.filter(s => s !== statusValue)
      } else {
        // Adicionar o status
        newStatus = [...currentStatus, statusValue]
      }
      
      // Se nenhum status estiver selecionado, manter pelo menos um padrão
      if (newStatus.length === 0) {
        newStatus = ['pending']
      }
      
      return { ...prev, status: newStatus }
    })
  }

  const handleMenuToggle = (contaId, e) => {
    if (e) e.stopPropagation()
    if (openMenuId === contaId) {
      setOpenMenuId(null)
      setMenuPosition(null)
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const menuWidth = 180
    let left = rect.right - menuWidth
    if (left < 8) left = 8

    let top = rect.bottom + 6
    const menuHeightEstimate = 130
    if (top + menuHeightEstimate > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeightEstimate - 6)
    }

    setMenuPosition({ top, left })
    setOpenMenuId(contaId)
  }

  const resetBaixaForm = () => {
    setContaParaBaixa(null)
    setBaixaFormData({
      paidAmount: '',
      paymentDate: toDateInputValue(new Date()),
      financialAccountId: ''
    })
    setErrors({})
  }

  const handleBaixaClick = (conta) => {
    const remaining = getPayableRemainingBalance(conta)
    const hasPhysicalAccount = conta.financial_account_type === 'physical' && conta.financial_account_id
    setContaParaBaixa(conta)
    setBaixaFormData({
      paidAmount: remaining.toFixed(2),
      paymentDate: toDateInputValue(new Date()),
      financialAccountId: hasPhysicalAccount ? String(conta.financial_account_id) : ''
    })
    setErrors({})
    setBaixaModalOpen(true)
  }

  const handleBaixa = async (e) => {
    if (e) e.preventDefault()
    if (!contaParaBaixa || baixaSubmitting) return

    const remaining = getPayableRemainingBalance(contaParaBaixa)
    const paidAmount = parseFloat(baixaFormData.paidAmount)
    const nextErrors = {}

    if (!Number.isFinite(paidAmount) || paidAmount <= 0) {
      nextErrors.paidAmount = 'Informe um valor maior que zero'
    } else if (paidAmount > remaining + 0.01) {
      nextErrors.paidAmount = 'O valor não pode ser maior que o saldo pendente'
    }

    if (!baixaFormData.paymentDate) {
      nextErrors.paymentDate = 'Informe a data do pagamento'
    }

    const needsAccount = !(contaParaBaixa.financial_account_type === 'physical' && contaParaBaixa.financial_account_id)
    if (needsAccount && contasFisicas.length === 0) {
      nextErrors.submit = 'Cadastre uma conta física antes de dar baixa'
    } else if (needsAccount && !baixaFormData.financialAccountId) {
      nextErrors.financialAccountId = 'Selecione a conta para dar baixa'
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    setBaixaSubmitting(true)
    setErrors({})
    try {
      const response = await accountPayableService.markAsPaid(
        contaParaBaixa.id,
        paidAmount,
        baixaFormData.paymentDate,
        baixaFormData.financialAccountId || contaParaBaixa.financial_account_id
      )

      if (response.success) {
        setBaixaModalOpen(false)
        resetBaixaForm()
        showToast(response.message || 'Baixa registrada com sucesso', 'success')
        loadContas()
      } else {
        setErrors({ submit: response.message || 'Erro ao dar baixa na conta' })
      }
    } catch (error) {
      console.error('Erro ao dar baixa:', error)
      setErrors({ submit: error.response?.data?.message || 'Erro ao dar baixa na conta' })
    } finally {
      setBaixaSubmitting(false)
    }
  }

  const handleMenuAction = (action, conta) => {
    setOpenMenuId(null)
    setMenuPosition(null)
    if (action === 'baixa') {
      handleBaixaClick(conta)
    } else if (action === 'edit') {
      handleEdit(conta)
    } else if (action === 'delete') {
      handleDelete(conta)
    }
  }

  const handleCopyId = async (code, accountId) => {
    if (!code) {
      showToast('Código não disponível', 'warning')
      return
    }
    
    try {
      await navigator.clipboard.writeText(code)
      setCopiedId(accountId)
      showToast(`Código ${code} copiado! Use para buscar no extrato.`, 'success')
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Erro ao copiar:', error)
      showToast('Erro ao copiar código', 'error')
    }
  }

  const handleUnlinkReconciliation = async (account, transaction) => {
    if (!account || !transaction) return
    
    const confirmed = window.confirm(
      `Tem certeza que deseja desvincular esta conta a pagar da transação do extrato?\n\n` +
      `Conta a Pagar: #${account.id}\n` +
      `Transação: ${transaction.description || 'N/A'}\n` +
      `Valor vinculado: ${formatCurrency(transaction.amount || 0)}\n\n` +
      `A conta voltará ao status "pendente" e o valor pago será limpo.`
    )
    
    if (!confirmed) return
    
    try {
      const response = await cashStatementService.unlinkReconciliation(
        transaction.transaction_id,
        account.id,
        'account_payable'
      )
      
      if (response && response.success) {
        showToast('Conta a pagar desvinculada com sucesso', 'success')
        loadContas() // Recarregar lista
      } else {
        showToast(response?.message || 'Erro ao desvincular conta a pagar', 'error')
      }
    } catch (error) {
      console.error('Erro ao desvincular conta a pagar:', error)
      showToast(error.response?.data?.message || 'Erro ao desvincular conta a pagar', 'error')
    }
  }

  const columns = [
    { key: 'description', label: 'Descrição', sortable: true },
    {
      key: 'origin',
      label: 'Origem',
      render: (value, row) => getOriginName(row)
    },
    {
      key: 'amount',
      label: 'Valor',
      className: 'erp-table-num',
      render: (value, row) => {
        const linkedTransactions = row.linked_transactions || []
        const hasLinks = linkedTransactions.length > 0
        const paidAmount = parseFloat(row.paid_amount || 0)
        const remainingBalance = getPayableRemainingBalance(row)
        const hasPartialPayment = isPartiallyPaidTitle(row)

        let displayValue = formatCurrency(value)

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span>{displayValue}</span>
              {hasPartialPayment && (
                <span style={{
                  fontSize: '0.7rem',
                  color: 'var(--color-warning, #d97706)',
                  fontWeight: 600
                }}>
                  Saldo: {formatCurrency(remainingBalance)}
                </span>
              )}
              {hasLinks && (
                <>
                  <span style={{
                    fontSize: '0.7rem',
                    color: 'var(--text-tertiary, #6b7280)',
                    fontStyle: 'italic'
                  }}>
                    ({linkedTransactions.length} vínculo{linkedTransactions.length > 1 ? 's' : ''}: {formatCurrency(linkedTransactions.reduce((sum, transaction) => sum + (parseFloat(transaction.amount) || 0), 0))})
                  </span>
                  {linkedTransactions.map((transaction, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleUnlinkReconciliation(row, transaction)
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--color-error, #ef4444)',
                        transition: 'color 0.2s',
                        marginLeft: '0.25rem'
                      }}
                      title={`Desvincular de: ${transaction.description || 'Transação'} (${formatCurrency(transaction.amount || 0)})`}
                    >
                      <Unlink size={14} />
                    </button>
                  ))}
                </>
              )}
            </div>
            {hasPartialPayment && (
              <span style={{
                fontSize: '0.68rem',
                color: 'var(--text-tertiary, #6b7280)'
              }}>
                Pago: {formatCurrency(paidAmount)} · Saldo: {formatCurrency(remainingBalance)}
              </span>
            )}
          </div>
        )
      }
    },
    {
      key: 'due_date',
      label: 'Vencimento',
      render: (value) => formatDateOnly(value)
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => getStatusBadge(value, row.due_date, row)
    },
    {
      key: 'id',
      label: 'CÓDIGO',
      render: (value, row) => {
        // Usar o ID da conta a pagar como código único para busca
        const accountId = value || row.id
        if (!accountId) {
          return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>
        }
        const isCopied = copiedId === row.id
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <code style={{ 
              fontSize: '0.75rem', 
              background: 'var(--bg-secondary, #f3f4f6)', 
              padding: '0.25rem 0.5rem', 
              borderRadius: '4px',
              fontFamily: 'monospace',
              color: 'var(--text-primary, #1f2937)'
            }}>
              {accountId}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCopyId(accountId.toString(), row.id)
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: isCopied ? 'var(--color-success, #10b981)' : 'var(--text-tertiary, #6b7280)',
                transition: 'color 0.2s'
              }}
              title="Copiar código da conta a pagar para buscar no extrato"
            >
              {isCopied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        )
      }
    },
    {
      key: 'actions',
      label: '',
      headerClassName: 'erp-table-actions-col',
      align: 'center',
      className: 'erp-table-actions-col',
      render: (value, row) => (
        <div className="erp-table-actions-cell">
          <button
            type="button"
            className={`erp-table-row-menu-btn${openMenuId === row.id ? ' is-open' : ''}`}
            onClick={(e) => handleMenuToggle(row.id, e)}
            aria-label="Ações da conta"
            aria-expanded={openMenuId === row.id}
          >
            <MoreVertical size={16} />
          </button>
        </div>
      )
    }
  ]

  const contaMenuAberta = openMenuId ? contas.find((c) => c.id === openMenuId) : null

  // --- Funções de importação Excel ---
  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      console.log('Arquivo selecionado:', file.name, file.size, 'bytes')
      setImportFile(file)
    }
  }

  const handleImportDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) {
      setImportFile(file)
    }
  }

  const handleImportPreview = async () => {
    if (!importFile) return
    setImportLoading(true)
    try {
      const response = await accountPayableService.previewImport(importFile)
      if (response.success && response.data?.success !== false) {
        setImportPreview(response.data)
        setImportStep('preview')
      } else {
        showToast(response.data?.error || response.message || 'Erro ao processar arquivo', 'error')
      }
    } catch (error) {
      console.error('Erro no preview de importação:', error)
      showToast(error.response?.data?.message || error.message || 'Erro ao processar arquivo', 'error')
    } finally {
      setImportLoading(false)
    }
  }

  const handleImportExecute = async () => {
    if (!importFile) return
    setImportLoading(true)
    try {
      const response = await accountPayableService.executeImport(importFile)
      if (response.success) {
        setImportResult(response.data)
        setImportStep('result')
        loadContas()
        showToast(`${response.data.created} contas importadas com sucesso`, 'success')
      } else {
        showToast(response.data?.error || 'Erro ao importar', 'error')
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao importar', 'error')
    } finally {
      setImportLoading(false)
    }
  }

  const handleImportClose = () => {
    setImportModalOpen(false)
    setImportStep('upload')
    setImportFile(null)
    setImportPreview(null)
    setImportResult(null)
    if (importFileInputRef.current) {
      importFileInputRef.current.value = ''
    }
  }

  return (
    <div className="contas-pagar-page">
      <div className="page-header">
        <div>
          <h1>Contas a Pagar</h1>
          <p>Gerencie suas contas a pagar</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate('/erp/financeiro/contas-pagar/nova')}
        >
          <Plus size={18} />
          Nova Conta
        </button>
      </div>

      {/* Toolbar — fora da tabela */}
      <div className="contas-pagar-toolbar">
        <div className="contas-pagar-search-wrapper">
          <Search className="contas-pagar-search-icon" size={18} />
          <input
            type="text"
            placeholder="Pesquisar contas a pagar..."
            className="contas-pagar-search-input"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <div className="contas-pagar-table-actions">
          <button
            onClick={() => setIsFilterOpen(true)}
            className={`contas-pagar-filter-button ${hasActiveFilters ? 'active' : ''}`}
          >
            <Filter size={14} /> Filtros
            {hasActiveFilters && (
              <span className="contas-pagar-filter-badge" />
            )}
          </button>
          <button
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className={`contas-pagar-filter-button ${hasDateRangeFilter ? 'active' : ''}`}
            title="Filtrar por período de vencimento"
          >
            <Calendar size={14} /> Período
            {hasDateRangeFilter && (
              <span className="contas-pagar-filter-badge" />
            )}
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={filters.dueDateFrom}
            endValue={filters.dueDateTo}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Vencimento"
            subtitle="Início e Fim"
          />
          <button className="contas-pagar-export-button" onClick={() => setImportModalOpen(true)}>
            <Upload size={14} /> Importar
          </button>
          <button className="contas-pagar-export-button">
            <Download size={14} /> Exportar
          </button>
        </div>
      </div>

      <ERPTable
        columns={columns}
        data={contas}
        loading={loading}
        emptyMessage="Nenhuma conta a pagar encontrada"
        loadingMessage="Carregando contas a pagar..."
        pagination={{
          page: 1,
          totalPages: 1,
          total: contas.length,
          showTotalCount: true
        }}
      />

      {contaMenuAberta && menuPosition && (
        <div
          className="contas-pagar-dropdown-menu"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          {canSettlePhysicalPayable(contaMenuAberta) && (
            <button type="button" onClick={() => handleMenuAction('baixa', contaMenuAberta)}>
              <DollarSign size={16} />
              Dar baixa
            </button>
          )}
          <button type="button" onClick={() => handleMenuAction('edit', contaMenuAberta)}>
            <Edit size={16} />
            Editar
          </button>
          <button type="button" onClick={() => handleMenuAction('delete', contaMenuAberta)} className="danger">
            <Trash2 size={16} />
            Excluir
          </button>
        </div>
      )}

      {/* Side Panel: Filtros Avançados */}
      {isFilterOpen && (
        <>
          <div className="contas-pagar-filter-overlay" onClick={() => setIsFilterOpen(false)} />
          <aside className="contas-pagar-filter-sidebar contas-pagar-scrollbar">
            <div className="contas-pagar-filter-header">
              <div className="contas-pagar-filter-header-content">
                <div className="contas-pagar-filter-header-icon">
                  <Filter size={24} />
                </div>
                <div>
                  <h3 className="contas-pagar-filter-header-title">
                    Filtros
                  </h3>
                  <p className="contas-pagar-filter-header-subtitle">
                    Refinar resultados
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsFilterOpen(false)} 
                className="contas-pagar-filter-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="contas-pagar-filter-content">
              {/* Status */}
              <div className="contas-pagar-filter-section">
                <h4 className="contas-pagar-filter-section-title">
                  Status (Selecione múltiplos)
                </h4>
                <div className="contas-pagar-filter-options">
                  {[
                    { id: 'pending', label: 'Pendente', icon: AlertCircle },
                    { id: 'overdue', label: 'Vencido', icon: AlertCircle },
                    { id: 'paid', label: 'Pago', icon: CheckCircle2 },
                    { id: 'cancelled', label: 'Cancelado', icon: X },
                  ].map(opt => {
                    const currentStatus = Array.isArray(filters.status) ? filters.status : [filters.status].filter(Boolean)
                    const isSelected = currentStatus.includes(opt.id)
                    return (
                      <button
                        key={opt.id}
                        onClick={() => toggleStatusFilter(opt.id)}
                        className={`contas-pagar-filter-option ${isSelected ? 'selected' : ''}`}
                      >
                        <div className="contas-pagar-filter-option-content">
                          <opt.icon 
                            size={18} 
                            className="contas-pagar-filter-option-icon"
                          />
                          <span className="contas-pagar-filter-option-label">
                            {opt.label}
                          </span>
                        </div>
                        {isSelected && (
                          <Check size={16} className="contas-pagar-filter-option-check" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Origem */}
              <div className="contas-pagar-filter-section">
                <h4 className="contas-pagar-filter-section-title">
                  Origem
                </h4>
                <div className="contas-pagar-filter-options">
                  {[
                    { id: '', label: 'Todas as Origens', icon: Layers },
                    { id: 'supplier', label: 'Fornecedor', icon: Sparkles },
                    { id: 'customer', label: 'Cliente', icon: Sparkles },
                    { id: 'user', label: 'Funcionário', icon: Sparkles },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => {
                        const originType = opt.id
                        setFilters({ 
                          ...filters, 
                          originType,
                          supplierId: originType === 'supplier' ? filters.supplierId : '',
                          customerId: originType === 'customer' ? filters.customerId : '',
                          userId: originType === 'user' ? filters.userId : ''
                        })
                      }}
                      className={`contas-pagar-filter-option ${filters.originType === opt.id ? 'selected' : ''}`}
                    >
                      <div className="contas-pagar-filter-option-content">
                        <opt.icon 
                          size={18} 
                          className="contas-pagar-filter-option-icon"
                        />
                        <span className="contas-pagar-filter-option-label">
                          {opt.label}
                        </span>
                      </div>
                      {filters.originType === opt.id && (
                        <Check size={16} className="contas-pagar-filter-option-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtro específico por origem */}
              {filters.originType === 'supplier' && (
                <div className="contas-pagar-filter-section">
                  <FilterLeftPicker
                    label="Fornecedor"
                    title="Fornecedor"
                    value={filters.supplierId}
                    options={supplierFilterOptions}
                    onChange={(value) => setFilters({ ...filters, supplierId: value })}
                    emptyOptionLabel="Todos os fornecedores"
                    searchable
                    searchPlaceholder="Buscar fornecedor..."
                    openOnHover
                  />
                </div>
              )}

              {filters.originType === 'customer' && (
                <div className="contas-pagar-filter-section">
                  <FilterLeftPicker
                    label="Cliente"
                    title="Cliente"
                    value={filters.customerId}
                    options={customerFilterOptions}
                    onChange={(value) => setFilters({ ...filters, customerId: value })}
                    emptyOptionLabel="Todos os clientes"
                    searchable
                    searchPlaceholder="Buscar cliente..."
                    openOnHover
                  />
                </div>
              )}

              {filters.originType === 'user' && (
                <div className="contas-pagar-filter-section">
                  <FilterLeftPicker
                    label="Funcionário"
                    title="Funcionário"
                    value={filters.userId}
                    options={userFilterOptions}
                    onChange={(value) => setFilters({ ...filters, userId: value })}
                    emptyOptionLabel="Todos os funcionários"
                    searchable
                    searchPlaceholder="Buscar funcionário..."
                    openOnHover
                  />
                </div>
              )}

              {/* Classificação */}
              <div className="contas-pagar-filter-section">
                <h4 className="contas-pagar-filter-section-title">
                  Classificação
                </h4>
                <div className="contas-pagar-filter-classification">
                  <FilterLeftPicker
                    label="Categoria"
                    title="Categoria"
                    value={filters.categoryFilterId}
                    options={categoryFilterOptions}
                    pinnedOptions={[
                      { value: FILTER_NONE_CATEGORY, label: 'Sem categoria', className: 'is-muted' }
                    ]}
                    onChange={(value) =>
                      setFilters({
                        ...filters,
                        categoryFilterId: value,
                        subcategoryFilterId: ''
                      })
                    }
                    emptyOptionLabel="Todas as categorias"
                    searchable
                    searchPlaceholder="Buscar categoria..."
                    openOnHover
                  />
                  <FilterLeftPicker
                    label="Subcategoria"
                    title="Subcategoria"
                    value={filters.subcategoryFilterId}
                    options={subcategoryFilterOptions}
                    onChange={(value) =>
                      setFilters({ ...filters, subcategoryFilterId: value })
                    }
                    emptyOptionLabel={
                      !filters.categoryFilterId || filters.categoryFilterId === FILTER_NONE_CATEGORY
                        ? 'Selecione uma categoria'
                        : filterSubcategorias.length === 0
                          ? 'Sem subcategorias'
                          : 'Todas as subcategorias'
                    }
                    searchable={filterSubcategorias.length > 0}
                    searchPlaceholder="Buscar subcategoria..."
                    disabled={
                      !filters.categoryFilterId ||
                      filters.categoryFilterId === FILTER_NONE_CATEGORY ||
                      filterSubcategorias.length === 0
                    }
                    openOnHover
                  />
                  <FilterLeftPicker
                    label="Centro de Custo"
                    title="Centro de Custo"
                    value={filters.costCenterId}
                    options={costCenterFilterOptions}
                    pinnedOptions={[
                      { value: FILTER_NONE_COST_CENTER, label: 'Sem centro de custo', className: 'is-muted' }
                    ]}
                    onChange={(value) =>
                      setFilters({ ...filters, costCenterId: value })
                    }
                    emptyOptionLabel="Todos os centros de custo"
                    searchable
                    searchPlaceholder="Buscar centro de custo..."
                    openOnHover
                  />
                </div>
              </div>
            </div>

            <div className="contas-pagar-filter-footer">
              <button 
                onClick={resetFilters}
                className="contas-pagar-filter-reset"
              >
                <Trash2 size={14} /> Limpar Filtros
              </button>
              <button 
                onClick={() => setIsFilterOpen(false)}
                className="contas-pagar-filter-apply"
              >
                Aplicar Filtros
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Modal de criação/edição */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingConta ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingConta ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="contas-pagar-edit-person-field">
            <label className="contas-pagar-edit-person-label">
              {PERSON_TYPE_LABELS[formData.personType] || 'Origem'}
            </label>
            <button
              type="button"
              className="contas-pagar-edit-person-picker"
              onClick={() => setShowPersonModal(true)}
            >
              <Search size={16} className="contas-pagar-edit-person-picker-icon" />
              <span className={formData.personName ? '' : 'contas-pagar-edit-person-placeholder'}>
                {formData.personName || 'Pesquisar por nome, CPF/CNPJ ou e-mail...'}
              </span>
            </button>
            {(errors.personId || errors.supplierId) && (
              <span className="contas-pagar-edit-person-error">
                {errors.personId || errors.supplierId}
              </span>
            )}
          </div>

          <FormField
            label="Descrição"
            name="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            error={errors.description}
          />

          <div className="form-row">
            <FormField
              label="Valor"
              name="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              error={errors.amount}
              required
            />

            <FormField
              label="Data de Vencimento"
              name="dueDate"
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              error={errors.dueDate}
              required
            />
          </div>

          <FormField
            label="Status"
            name="status"
            type="select"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            error={errors.status}
            options={[
              { value: 'pending', label: 'Pendente' },
              { value: 'paid', label: 'Pago' },
              { value: 'overdue', label: 'Vencido' },
              { value: 'cancelled', label: 'Cancelado' }
            ]}
          />

          <FormField
            label="Categoria"
            name="categoryId"
            type="select"
            value={formData.categoryId}
            onChange={(e) =>
              setFormData({ ...formData, categoryId: e.target.value, subcategoryId: '' })
            }
            error={errors.categoryId}
            options={categoryOptions}
          />

          {formData.categoryId && subcategoryOptions.length > 1 && (
            <FormField
              label="Subcategoria"
              name="subcategoryId"
              type="select"
              value={formData.subcategoryId}
              onChange={(e) => setFormData({ ...formData, subcategoryId: e.target.value })}
              error={errors.subcategoryId}
              options={subcategoryOptions}
            />
          )}

          <FormField
            label="Centro de Custo"
            name="costCenterId"
            type="select"
            value={formData.costCenterId}
            onChange={(e) => setFormData({ ...formData, costCenterId: e.target.value })}
            error={errors.costCenterId}
            options={costCenterOptions}
          />

          <FormField
            label="Conta bancária"
            name="financialAccountId"
            type="select"
            value={formData.financialAccountId}
            onChange={(e) => setFormData({ ...formData, financialAccountId: e.target.value })}
            error={errors.financialAccountId}
            options={financialAccountOptions}
          />

          <div className="form-row">
            <FormField
              label="Taxa de Juros (%)"
              name="interestRate"
              type="number"
              step="0.01"
              min="0"
              value={formData.interestRate}
              onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
              error={errors.interestRate}
            />

            <FormField
              label="Taxa de Multa (%)"
              name="fineRate"
              type="number"
              step="0.01"
              min="0"
              value={formData.fineRate}
              onChange={(e) => setFormData({ ...formData, fineRate: e.target.value })}
              error={errors.fineRate}
            />
          </div>

          <FormField
            label="Observações"
            name="observations"
            type="textarea"
            value={formData.observations}
            onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
            error={errors.observations}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>

      {/* Modal de Importação Excel */}
      <Modal
        isOpen={importModalOpen}
        onClose={handleImportClose}
        title="Importar Contas a Pagar"
        size="large"
        footer={
          <>
            {importStep === 'upload' && (
              <>
                <button className="btn-secondary" onClick={handleImportClose}>Cancelar</button>
                <button className="btn-primary" onClick={handleImportPreview} disabled={!importFile || importLoading}>
                  {importLoading ? 'Processando...' : 'Visualizar'}
                </button>
              </>
            )}
            {importStep === 'preview' && (
              <>
                <button className="btn-secondary" onClick={() => { setImportStep('upload'); setImportPreview(null) }}>Voltar</button>
                <button className="btn-primary" onClick={handleImportExecute} disabled={importLoading || !importPreview?.validRows}>
                  {importLoading ? 'Importando...' : `Importar ${importPreview?.validRows || 0} contas`}
                </button>
              </>
            )}
            {importStep === 'result' && (
              <button className="btn-primary" onClick={handleImportClose}>Fechar</button>
            )}
          </>
        }
      >
        {importStep === 'upload' && (
          <div className="import-upload-area">
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFileSelect}
              style={{ display: 'none' }}
            />
            <div
              className="import-dropzone"
              onDrop={handleImportDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => importFileInputRef.current?.click()}
            >
              <FileSpreadsheet size={48} strokeWidth={1.5} />
              <p><strong>Arraste o arquivo Excel aqui</strong></p>
              <p>ou clique para selecionar</p>
              <span className="import-dropzone-hint">Formatos aceitos: .xlsx, .xls</span>
            </div>
            {importFile && (
              <div className="import-file-selected">
                <FileSpreadsheet size={16} />
                <span>{importFile.name}</span>
                <button onClick={() => setImportFile(null)}><X size={14} /></button>
              </div>
            )}
            <div className="import-instructions">
              <h4>Formato esperado da planilha:</h4>
              <table className="import-format-table">
                <thead>
                  <tr>
                    <th>Nome/Razão social</th>
                    <th>Descrição</th>
                    <th>Conta</th>
                    <th>Parcela</th>
                    <th>Data vencimento</th>
                    <th>Valor parcela</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>DELLAMED S.A.</td>
                    <td>Conta ref. nota 609</td>
                    <td>PAGSEGURO...</td>
                    <td>4/5</td>
                    <td>01/06/2026</td>
                    <td>3.305,62</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importStep === 'preview' && importPreview && (
          <div className="import-preview-area">
            <div className="import-preview-summary">
              <div className="import-stat">
                <span className="import-stat-value">{importPreview.totalRows}</span>
                <span className="import-stat-label">Total de linhas</span>
              </div>
              <div className="import-stat success">
                <span className="import-stat-value">{importPreview.validRows}</span>
                <span className="import-stat-label">Válidas</span>
              </div>
              {importPreview.errorRows > 0 && (
                <div className="import-stat error">
                  <span className="import-stat-value">{importPreview.errorRows}</span>
                  <span className="import-stat-label">Com erros</span>
                </div>
              )}
            </div>

            <div className="import-preview-table-wrapper">
              <table className="import-preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fornecedor</th>
                    <th>Descrição</th>
                    <th>Conta</th>
                    <th>Parcela</th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.map((row) => (
                    <tr key={row.rowNumber} className={!row.valid ? 'import-row-error' : row.warnings.length > 0 ? 'import-row-warning' : ''}>
                      <td>{row.rowNumber}</td>
                      <td>
                        {row.supplierName}
                        {row.supplierName && !row.supplierId && <span className="import-badge warn" title="Fornecedor não encontrado">!</span>}
                      </td>
                      <td>{row.description}</td>
                      <td>
                        {row.accountName}
                        {row.accountName && !row.financialAccountId && <span className="import-badge warn" title="Conta não encontrada">!</span>}
                      </td>
                      <td>{row.installment}</td>
                      <td>{row.dueDateFormatted}</td>
                      <td>{row.amountFormatted}</td>
                      <td>
                        {row.valid ? (
                          <span className="import-badge ok"><CheckCircle2 size={12} /> OK</span>
                        ) : (
                          <span className="import-badge err" title={row.errors.join(', ')}><AlertCircle size={12} /> Erro</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importStep === 'result' && importResult && (
          <div className="import-result-area">
            <div className="import-result-icon">
              <CheckCircle2 size={48} />
            </div>
            <h3>Importação Concluída</h3>
            <div className="import-result-stats">
              <p><strong>{importResult.created}</strong> contas criadas com sucesso</p>
              {importResult.skipped > 0 && <p><strong>{importResult.skipped}</strong> linhas ignoradas (erros de validação)</p>}
              {importResult.errors?.length > 0 && (
                <div className="import-result-errors">
                  <p>Erros durante a importação:</p>
                  <ul>
                    {importResult.errors.map((err, i) => (
                      <li key={i}>Linha {err.rowNumber}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={baixaModalOpen}
        onClose={() => {
          if (baixaSubmitting) return
          setBaixaModalOpen(false)
          resetBaixaForm()
        }}
        title="Dar baixa na conta"
        size="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              disabled={baixaSubmitting}
              onClick={() => {
                setBaixaModalOpen(false)
                resetBaixaForm()
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={baixaSubmitting || (
                !(contaParaBaixa?.financial_account_type === 'physical' && contaParaBaixa?.financial_account_id)
                && contasFisicas.length === 0
              )}
              onClick={handleBaixa}
            >
              {baixaSubmitting ? 'Confirmando...' : 'Confirmar baixa'}
            </button>
          </>
        }
      >
        {contaParaBaixa && (
          <form onSubmit={handleBaixa}>
            <div className="baixa-info">
              <p><strong>Descrição:</strong> {contaParaBaixa.description || contaParaBaixa.observation || '-'}</p>
              <p><strong>Valor original:</strong> {formatCurrency(contaParaBaixa.amount)}</p>
              <p><strong>Saldo pendente:</strong> {formatCurrency(getPayableRemainingBalance(contaParaBaixa))}</p>
              {contaParaBaixa.due_date && (
                <p><strong>Vencimento:</strong> {formatDateOnly(contaParaBaixa.due_date)}</p>
              )}
              {contaParaBaixa.financial_account_type === 'physical' && contaParaBaixa.financial_account_name && (
                <p><strong>Conta física:</strong> {contaParaBaixa.financial_account_name}</p>
              )}
            </div>

            {!(contaParaBaixa.financial_account_type === 'physical' && contaParaBaixa.financial_account_id) && (
              contasFisicas.length === 0 ? (
                <div className="baixa-account-empty">
                  <p>Nenhuma conta física cadastrada. A baixa precisa de uma conta relacionada.</p>
                  <Link to="/erp/financeiro/contas">Ir para Contas</Link>
                </div>
              ) : (
                <FormField
                  label="Conta"
                  name="financialAccountId"
                  type="select"
                  value={baixaFormData.financialAccountId}
                  onChange={(e) => setBaixaFormData({ ...baixaFormData, financialAccountId: e.target.value })}
                  error={errors.financialAccountId}
                  required
                  options={[
                    { value: '', label: 'Selecione a conta' },
                    ...contasFisicas.map((account) => ({
                      value: String(account.id),
                      label: account.name
                    }))
                  ]}
                />
              )
            )}

            <FormField
              label="Valor pago"
              name="paidAmount"
              type="number"
              step="0.01"
              min="0.01"
              value={baixaFormData.paidAmount}
              onChange={(e) => setBaixaFormData({ ...baixaFormData, paidAmount: e.target.value })}
              error={errors.paidAmount}
              required
            />

            <FormField
              label="Data de pagamento"
              name="paymentDate"
              type="date"
              value={baixaFormData.paymentDate}
              onChange={(e) => setBaixaFormData({ ...baixaFormData, paymentDate: e.target.value })}
              error={errors.paymentDate}
              required
            />

            {errors.submit && (
              <div className="form-error-message">{errors.submit}</div>
            )}
          </form>
        )}
      </Modal>

      <SelectPersonModal
        isOpen={showPersonModal}
        onClose={() => setShowPersonModal(false)}
        onSelect={handleSelectPerson}
        selectedType={formData.personType}
        selectedId={formData.personId}
      />

      {/* Toast para notificações */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  )
}

export default ContasPagarPage

